import { useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { toast } from "react-toastify";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { PageHeader, Card, ActionButton } from "../shared";

const ConnectClub = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const formik = useFormik({
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string()
        .required("Club invite code is required")
        .matches(/^CLB-/i, "Enter a CLB- club invite code"),
    }),
    onSubmit: async (values) => {
      if (!token) return;
      setLoading(true);
      setError("");

      const headers = {
        "Content-Type": "application/json",
        "x-access-token": token,
      };

      try {
        const data = await ApiServices.PostApiCall(
          ApiEndpoint.MASTER_ORG_CONNECT,
          JSON.stringify({ code: values.code.trim() }),
          headers
        );

        if (data?.status === true) {
          toast.success(data.message || "Connected to club successfully");
          navigate("/master-org/sub-orgs");
        } else {
          const msg = data?.message || "Failed to connect with club";
          setError(msg);
          toast.error(msg);
        }
      } catch {
        setError("An error occurred. Please try again.");
        toast.error("Connection failed. Please try again.");
      } finally {
        setLoading(false);
      }
    },
  });

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "#242424" : "#f7fafc",
      borderRadius: "8px",
      "& fieldset": {
        borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: isDark ? "#6366f1" : "#1976d2",
      },
      "&.Mui-focused fieldset": {
        borderColor: isDark ? "#818cf8" : "#1976d2",
        borderWidth: "2px",
      },
    },
    "& .MuiInputLabel-root": {
      color: isDark ? "#cbd5e1" : "#718096",
    },
    "& .MuiInputBase-input": {
      color: isDark ? "#ffffff" : "#1a202c",
    },
  };

  if (userType !== "master_org") {
    return (
      <Box>
        <PageHeader title="Connect with Club" subtitle="Access denied." />
        <Card>
          <Typography sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            This page is for club partner accounts only.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Connect with a Club"
        subtitle="Enter a CLB- invite code from a club to connect your club partner"
        breadcrumbs={[{ label: "Club Partner" }, { label: "Connect" }]}
      />

      <Card title="Club Invite Code">
        <Typography
          sx={{
            mb: 2,
            fontSize: "0.875rem",
            color: isDark ? "#cbd5e1" : "#5a6a85",
          }}
        >
          If a club sent you a CLB- invite code, enter it below. Your connection
          will be approved automatically — no join request is needed.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 480 }}>
            <TextField
              fullWidth
              size="small"
              id="code"
              name="code"
              label="Club Invite Code (CLB-...)"
              placeholder="CLB-XXXX-XXXX"
              value={formik.values.code}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.code && Boolean(formik.errors.code)}
              helperText={formik.touched.code && formik.errors.code}
              InputProps={{
                startAdornment: (
                  <VpnKeyIcon
                    sx={{ mr: 1, color: isDark ? "#718096" : "#cbd5e1", fontSize: 20 }}
                  />
                ),
              }}
              sx={fieldSx}
            />

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <ActionButton type="submit" disabled={loading}>
                {loading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Connect"}
              </ActionButton>
              <ActionButton
                variant="secondary"
                type="button"
                onClick={() => navigate("/master-org/dashboard")}
              >
                Cancel
              </ActionButton>
            </Box>
          </Box>
        </form>
      </Card>
    </Box>
  );
};

export default ConnectClub;
